import React from 'react'
import { Textarea } from '../../components/textarea'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UITextareaProps extends React.ComponentProps<typeof Textarea> {
  /**
   * 라벨
   */
  label?: string
  /**
   * 라벨 ID
   */
  labelId?: string
  /**
   * 설명 텍스트
   */
  description?: string
  /**
   * 오류 메시지
   */
  error?: string
  /**
   * 도움말 텍스트
   */
  helperText?: string
  /**
   * 필수 여부
   */
  required?: boolean
  /**
   * 글자 수 제한
   */
  maxLength?: number
  /**
   * 글자 수 표시 여부
   */
  showCharCount?: boolean
  /**
   * 크기 조절 가능 여부
   */
  resizable?: boolean
  /**
   * 컨테이너 클래스명
   */
  containerClassName?: string
}

/**
 * 텍스트 영역 컴포넌트
 * 라벨, 도움말 텍스트, 오류 메시지, 글자 수 표시를 지원합니다.
 */
export const UITextarea = React.forwardRef<
  React.ComponentRef<typeof Textarea>,
  UITextareaProps
>(({
  className,
  label,
  labelId,
  description,
  error,
  helperText,
  required = false,
  maxLength,
  showCharCount = false,
  resizable = true,
  containerClassName,
  value,
  ...props
}, ref) => {
  const textareaId = labelId || props.id
  const currentLength = typeof value === 'string' ? value.length : 0

  if (!label && !description && !error && !helperText && !showCharCount) {
    return (
      <Textarea
        ref={ref}
        className={cn(
          !resizable && "resize-none",
          className
        )}
        maxLength={maxLength}
        aria-invalid={error ? 'true' : undefined}
        value={value}
        {...props}
      />
    )
  }

  return (
    <UIBox className={cn("space-y-2", containerClassName)}>
      {label && (
        <UILabel
          htmlFor={textareaId}
          className={cn(
            "text-sm font-medium leading-none",
            required && "after:content-['*'] after:ml-0.5 after:text-destructive",
            error && "text-destructive"
          )}
        >
          {label}
        </UILabel>
      )}

      {description && (
        <UITypography className="text-xs text-muted-foreground">{description}</UITypography>
      )}

      <Textarea
        ref={ref}
        id={textareaId}
        className={cn(
          !resizable && "resize-none",
          error && "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20",
          className
        )}
        maxLength={maxLength}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={
          error ? `${textareaId}-error` :
          helperText ? `${textareaId}-helper` :
          undefined
        }
        value={value}
        {...props}
      />

      <UIBox className="flex items-center justify-between">
        <UIBox className="flex-1">
          {error && (
            <UITypography
              as="p"
              className="text-xs text-destructive"
              id={`${textareaId}-error`}
              role="alert"
            >
              {error}
            </UITypography>
          )}

          {helperText && !error && (
            <UITypography
              as="p"
              className="text-xs text-muted-foreground"
              id={`${textareaId}-helper`}
            >
              {helperText}
            </UITypography>
          )}
        </UIBox>

        {showCharCount && maxLength && (
          <UIBox className={cn(
            "text-xs",
            currentLength > maxLength * 0.9 ? "text-warning" : "text-muted-foreground",
            currentLength >= maxLength ? "text-destructive" : ""
          )}>
            {currentLength}/{maxLength}
          </UIBox>
        )}
      </UIBox>
    </UIBox>
  )
})

UITextarea.displayName = "UITextarea"